/**
 * News (Новости) — unified announcements board.
 *
 * Tamagui port of the handoff `NewsScreen` (screen-life.jsx):
 *  • segmented control: Feed (social stream) / Cards (editorial grid)
 *  • Feed view: composer (HR/admin only — gated on /users/me roles) → POST
 *    /feed/posts, channel filter chips, featured editorial hero, post cards
 *    with a like action (dislikes + comments are hidden by product decision)
 *  • Cards view: grid of editorial news cards
 *
 * The social stream renders live `/feed/posts` only (list / react / create);
 * an empty or unreachable response shows the empty state. Editorial cards read
 * live `/api/news`. Tapping a post opens the post detail (`/post/[id]`).
 */
import { useState } from 'react'
import { Image, Platform } from 'react-native'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { Input, Stack, Text, XStack, YStack } from 'tamagui'

import { useApp, type Localized } from '../../src/i18n'
import { useInfinitePosts, useReact, useCreatePost, useUploadFile } from '../../src/features/feed/useFeed'
import { useInfiniteNews } from '../../src/features/news/useNews'
import { api } from '../../src/api/client'
import { useMe } from '../../src/features/users/useUsers'
import { canCreateNews } from '../../src/auth/roles'
import { CHANNELS, CHANNEL_LABEL, CHANNEL_TONE } from '../../src/features/feed/channels'
import { ReactionButton } from '../../src/features/feed/ReactionButton'
import { pickFileWeb } from '../../src/lib/files'
import { tone, type Tone } from '../../src/theme/tones'
import type { Post, NewsItem as ApiNews, Attachment } from '../../src/lib/types'
import { timeAgo } from '../../src/lib/format'
import { Avatar, Button, Card, Chip, Enter, FadeIn, FormTextArea, Icon, Seg, Skeleton, SkeletonLines, Tag } from '../../src/ui'
import { Page } from '../../src/ui/Screen'

type View = 'news' | 'posts'

interface PostVM {
  id: string
  title?: string
  author: string
  role?: string
  when: string
  body: string
  catLabel: string
  tone: Tone
  likes: number
}

const liveVM = (p: Post, t: (v: Localized) => string): PostVM => ({
  id: p.id,
  title: p.title,
  author: p.author.fullName,
  role: p.author.jobTitle ?? undefined,
  when: timeAgo(p.createdAt),
  body: p.body,
  catLabel: t(CHANNEL_LABEL[p.channel] ?? p.channel),
  tone: CHANNEL_TONE[p.channel] ?? 'purple',
  likes: p.likes,
})

// ── Editorial news (live /api/news) ───────────────────────────────────────────
interface NewsVM {
  id: string
  title: string
  excerpt: string
  catLabel: string
  tone: Tone
  coverUrl?: string | null
  author: string
  when: string
}

const liveNewsVM = (n: ApiNews, l: (k: string) => string): NewsVM => ({
  id: n.id,
  title: n.title,
  excerpt: n.excerpt ?? '',
  catLabel: l('nav.news'),
  tone: n.isPinned ? 'gold' : 'purple',
  coverUrl: api.assetUrl(n.coverUrl),
  author: n.author?.fullName ?? '',
  when: timeAgo(n.publishedAt ?? n.createdAt),
})

// ── Segmented view toggle ────────────────────────────────────────────────────
function ViewToggle({ view, onChange }: { view: View; onChange: (v: View) => void }) {
  const { l } = useApp()
  return (
    <XStack backgroundColor="$bgLayout" padding={4} borderRadius={7} gap={4}>
      <Seg active={view === 'news'} label={l('news.tabNews')} icon="layout-grid" onPress={() => onChange('news')} />
      <Seg active={view === 'posts'} label={l('news.tabPosts')} icon="list" onPress={() => onChange('posts')} />
    </XStack>
  )
}

// ── Composer ─────────────────────────────────────────────────────────────────
function Composer() {
  const { t, l } = useApp()
  const { data: me } = useMe()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [channel, setChannel] = useState<string>('general')

  const [atts, setAtts] = useState<Attachment[]>([])
  const uploadFile = useUploadFile()

  const create = useCreatePost(() => {
    setTitle('')
    setBody('')
    setAtts([])
  })

  const onAttach = async () => {
    const f = await pickFileWeb('image/*')
    if (!f) return
    try {
      const a = await uploadFile.mutateAsync(f)
      setAtts((x) => [...x, a])
    } catch {
      /* upload failed — surfaced via uploadFile.isError if needed */
    }
  }

  const canPost = title.trim().length >= 3 && body.trim().length >= 1 && !create.isPending
  const submit = () => {
    if (!canPost) return
    create.mutate({
      title: title.trim(),
      body: body.trim(),
      channel,
      attachmentIds: atts.length ? atts.map((a) => a.id) : undefined,
    })
  }

  return (
    <Card padding={16}>
      <XStack gap={12}>
        <Avatar name={me?.fullName || '—'} seed="me" uri={me?.avatarUrl} />
        <YStack flex={1} gap={8}>
          <Input
            value={title}
            onChangeText={setTitle}
            placeholder={l('feed.titlePlaceholder')}
            placeholderTextColor="$tertiary"
            height={38}
            fontSize={14}
            fontWeight="600"
            color="$color"
            backgroundColor="$bgLayout"
            borderWidth={1}
            borderColor="$borderColor"
            borderRadius={7}
            paddingHorizontal={11}
            focusStyle={{ borderColor: '$primary' }}
          />
          <FormTextArea value={body} onChangeText={setBody} placeholder={l('feed.placeholder')} />
        </YStack>
      </XStack>

      {atts.length > 0 && (
        <XStack marginTop={10} paddingLeft={52} gap={8} flexWrap="wrap">
          {atts.map((a) => (
            <XStack
              key={a.id}
              alignItems="center"
              gap={6}
              paddingHorizontal={10}
              paddingVertical={5}
              borderRadius={999}
              backgroundColor="$bgLayout"
              borderWidth={1}
              borderColor="$borderColor"
            >
              <Icon name="image" size={13} color="$muted" />
              <Text fontSize={11} color="$muted" maxWidth={160} numberOfLines={1}>
                {a.name}
              </Text>
              <Stack onPress={() => setAtts((x) => x.filter((y) => y.id !== a.id))} cursor="pointer">
                <Icon name="x" size={13} color="$tertiary" />
              </Stack>
            </XStack>
          ))}
        </XStack>
      )}

      <XStack marginTop={12} paddingLeft={52} alignItems="center" gap={7} flexWrap="wrap">
        {CHANNELS.map((c) => {
          const on = channel === c
          return (
            <XStack
              key={c}
              onPress={() => setChannel(c)}
              cursor="pointer"
              animation="quick"
              alignItems="center"
              paddingHorizontal={10}
              paddingVertical={4}
              borderRadius={999}
              backgroundColor={on ? tone(CHANNEL_TONE[c]).soft : 'transparent'}
              borderWidth={1}
              borderColor={on ? 'transparent' : '$borderSecondary'}
            >
              <Text fontSize={11} fontWeight="700" color={on ? tone(CHANNEL_TONE[c]).text : '$muted'}>
                {t(CHANNEL_LABEL[c])}
              </Text>
            </XStack>
          )
        })}
        {Platform.OS === 'web' && (
          <XStack
            onPress={onAttach}
            cursor="pointer"
            animation="quick"
            alignItems="center"
            gap={6}
            paddingHorizontal={10}
            paddingVertical={6}
            borderRadius={5}
            opacity={uploadFile.isPending ? 0.5 : 1}
            hoverStyle={{ backgroundColor: '$backgroundHover' }}
          >
            <Icon name="paperclip" size={15} color="$muted" />
            <Text fontSize={12} fontWeight="600" color="$muted">
              {uploadFile.isPending ? t({ ru: 'Загрузка…', en: 'Uploading…' }) : t({ ru: 'Фото', en: 'Photo' })}
            </Text>
          </XStack>
        )}
        <Stack flex={1} minWidth={8} />
        <Button variant="primary" onPress={submit} opacity={canPost ? 1 : 0.5} cursor={canPost ? 'pointer' : 'default'}>
          {create.isPending ? l('feed.posting') : l('feed.post')}
        </Button>
      </XStack>
    </Card>
  )
}

// ── Post card (live or demo) ─────────────────────────────────────────────────
function PostCard({ post, onOpen }: { post: PostVM; onOpen?: () => void }) {
  const { l } = useApp()
  const react = useReact()
  const like = () => react.mutate({ id: post.id, type: 'LIKE' })

  return (
    <Card
      padding={16}
      // Hover lift: the card leads to the post detail (body tap), so it gets the
      // standard clickable-card affordance. Transform + shadow only.
      {...(onOpen
        ? {
            animation: 'quicker' as const,
            hoverStyle: { borderColor: '$primary', y: -2, shadowOpacity: 0.2, shadowRadius: 12 },
          }
        : null)}
    >
      <XStack alignItems="center" gap={10}>
        <Avatar name={post.author} seed={post.author} />
        <YStack flex={1} minWidth={0}>
          <Text fontSize={14} fontWeight="600" color="$color" numberOfLines={1}>
            {post.author}
          </Text>
          <Text fontSize={11} color="$muted" numberOfLines={1}>
            {post.role ? `${post.role} · ` : ''}
            {post.when}
          </Text>
        </YStack>
        <Tag tone={post.tone}>{post.catLabel}</Tag>
      </XStack>

      <YStack
        onPress={onOpen}
        cursor={onOpen ? 'pointer' : 'default'}
        gap={6}
        marginTop={12}
        {...(onOpen ? { animation: 'quicker' as const, hoverStyle: { opacity: 0.85 }, pressStyle: { scale: 0.99, opacity: 0.7 } } : null)}
      >
        {post.title && (
          <Text fontSize={16} fontWeight="700" color="$color" lineHeight={21}>
            {post.title}
          </Text>
        )}
        <Text fontSize={14} color="$color" lineHeight={21} numberOfLines={6}>
          {post.body}
        </Text>
      </YStack>

      <XStack gap={2} marginTop={12} paddingTop={8} borderTopWidth={1} borderColor="$borderSecondary">
        <ReactionButton icon="heart" count={post.likes} onPress={like} />
        <Stack flex={1} />
        <ReactionButton icon="share-2" label={l('c.share')} />
      </XStack>
    </Card>
  )
}

// ── Editorial cards grid ─────────────────────────────────────────────────────
function NewsCardsGrid({ items, onOpen }: { items: NewsVM[]; onOpen?: (id: string) => void }) {
  const { l } = useApp()
  const isWeb = Platform.OS === 'web'
  return (
    <YStack gap={16}>
      {/* Web: real CSS grid → every column is the same width and a card alone in the
          last row keeps its column width. The old flex-wrap let a lone card flex-grow
          out to maxWidth (wider) and only match its own height (shorter). Native:
          flex-wrap fallback (RN has no CSS grid). */}
      <Stack
        gap={16}
        {...(isWeb
          ? { display: 'grid' as 'flex', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }
          : { flexDirection: 'row', flexWrap: 'wrap' })}
      >
        {items.map((n, i) => {
          const c = tone(n.tone)
          // Cover image when present (else the tone stripe); title capped at 2 lines
          // and the author row pinned to the bottom (flex spacer) so cards share a height.
          // Enter carries the grid-item sizing (was on Card) so the stagger wrapper
          // doesn't change layout; the Card fills it.
          return (
            <Enter
              key={`news-${n.id}`}
              index={Math.min(i, 11)}
              flexGrow={1}
              flexBasis={300}
              minWidth={260}
              maxWidth={420}
            >
              <Card
                flex={1}
                overflow="hidden"
                padding={0}
                {...(onOpen
                  ? {
                      animation: 'quicker' as const,
                      hoverStyle: { borderColor: '$primary', y: -2, shadowOpacity: 0.2, shadowRadius: 12 },
                    }
                  : null)}
              >
              {n.coverUrl ? (
                <Image source={{ uri: n.coverUrl }} style={{ width: '100%', height: 140 }} resizeMode="cover" />
              ) : (
                <Stack height={8} backgroundColor={c.solid} />
              )}
              <YStack padding={16} gap={6} flex={1}>
                <Tag tone={n.tone}>{n.catLabel}</Tag>
                <Text fontSize={15} fontWeight="700" color="$color" lineHeight={20} marginTop={4} numberOfLines={2}>
                  {n.title}
                </Text>
                {!!n.excerpt && (
                  <Text fontSize={13} color="$muted" lineHeight={19} numberOfLines={3}>
                    {n.excerpt}
                  </Text>
                )}
                <Stack flex={1} minHeight={6} />
                <XStack alignItems="center" gap={8}>
                  <Avatar name={n.author || '—'} seed={n.id} size="xs" />
                  <Text fontSize={11} color="$muted" numberOfLines={1}>
                    {n.author}
                    {n.when ? ` · ${n.when}` : ''}
                  </Text>
                </XStack>
                {/* Explicit detail entry point → opens the full article at /news/[id].
                    Live only: demo items have no GET /news/:id, so it's shown inert + dimmed. */}
                <XStack
                  onPress={onOpen ? () => onOpen(n.id) : undefined}
                  cursor={onOpen ? 'pointer' : 'default'}
                  opacity={onOpen ? 1 : 0.45}
                  animation="quick"
                  alignSelf="flex-start"
                  alignItems="center"
                  gap={6}
                  marginTop={12}
                  paddingVertical={7}
                  paddingHorizontal={12}
                  borderRadius={6}
                  backgroundColor="$bgLayout"
                  hoverStyle={onOpen ? { backgroundColor: '$backgroundHover' } : {}}
                  pressStyle={onOpen ? { scale: 0.97 } : {}}
                >
                  <Text fontSize={12} fontWeight="700" color="$primary">
                    {l('c.readMore')}
                  </Text>
                  <Icon name="arrow-right" size={14} color="$primary" />
                </XStack>
              </YStack>
              </Card>
            </Enter>
          )
        })}
      </Stack>
    </YStack>
  )
}

export default function NewsScreen() {
  const { t, l } = useApp()
  const router = useRouter()
  // Tab lives in the URL (not local state) so router.back() from /post/[id] or
  // /news/[id] restores the tab the user was on instead of resetting to 'news'.
  const { tab } = useLocalSearchParams<{ tab?: string }>()
  const view: View = tab === 'posts' ? 'posts' : 'news'
  const setView = (v: View) => router.setParams({ tab: v })
  const [channel, setChannel] = useState<string>('all')

  // Композер создания новостей виден только HR/admin (роли из /users/me).
  const { data: me } = useMe()

  const {
    data: livePosts,
    isLoading,
    isError,
    hasNextPage: hasMorePosts,
    isFetchingNextPage: isFetchingMorePosts,
    fetchNextPage: fetchMorePosts,
  } = useInfinitePosts(channel === 'all' ? undefined : channel)
  const posts: PostVM[] = (livePosts ?? []).map((p) => liveVM(p, t))

  // Editorial news — live /api/news only.
  const {
    data: liveNews,
    isLoading: newsLoading,
    hasNextPage: hasMoreNews,
    isFetchingNextPage: isFetchingMoreNews,
    fetchNextPage: fetchMoreNews,
  } = useInfiniteNews()
  const newsVMs: NewsVM[] = (liveNews ?? []).map((n) => liveNewsVM(n, l))

  return (
    <Page
      // Title tracks the active tab (Новости / Посты) instead of a fixed label.
      title={view === 'news' ? l('news.tabNews') : l('news.tabPosts')}
      subtitle={l('news.subtitle')}
      action={<ViewToggle view={view} onChange={setView} />}
      // Constant page width keeps the header + toggle anchored across tabs; the
      // posts feed is narrowed below (centered) so the toggle no longer jumps.
      maxWidth={1180}
    >
      {/* Keyed on the active tab → switching tabs remounts the content with a
          fade+rise and re-runs the card cascades below. */}
      <FadeIn key={view}>
      {view === 'news' ? (
        newsVMs.length === 0 ? (
          newsLoading ? (
            // shimmer placeholders shaped like the news grid
            <XStack gap={16} flexWrap="wrap">
              {[0, 1, 2].map((i) => (
                <Card key={i} flexGrow={1} flexBasis={320} minWidth={280} padding={16} gap={12}>
                  <Skeleton height={140} borderRadius={7} />
                  <SkeletonLines lines={2} />
                </Card>
              ))}
            </XStack>
          ) : (
            <Card padding={40} alignItems="center">
              <Text fontSize={13} color="$muted">
                {l('news.empty')}
              </Text>
            </Card>
          )
        ) : (
          <YStack gap={16}>
            <NewsCardsGrid items={newsVMs} onOpen={(id) => router.push(`/news/${id}`)} />
            {hasMoreNews && (
              <Button alignSelf="center" disabled={isFetchingMoreNews} onPress={() => fetchMoreNews()}>
                {isFetchingMoreNews ? t({ ru: 'Загрузка…', en: 'Loading…' }) : t({ ru: 'Показать ещё', en: 'Show more' })}
              </Button>
            )}
          </YStack>
        )
      ) : (
        // Posts feed keeps its narrower reading width, centered within the
        // constant-width page so the header/toggle stay anchored across tabs.
        <YStack width="100%" maxWidth={720} alignSelf="center" gap={20}>
          {canCreateNews(me) && <Composer />}

          <XStack gap={8} flexWrap="wrap">
            {/* dim — у фильтра каналов нет состояния "ничего не выбрано" (all — дефолт),
                поэтому off-чип всегда приглушён, а не с акцентным бордером */}
            <Chip on={channel === 'all'} dim animation="quick" hoverStyle={{ borderColor: '$primary' }} pressStyle={{ scale: 0.96 }} onPress={() => setChannel('all')}>
              {l('news.allChannels')}
            </Chip>
            {CHANNELS.map((c) => (
              <Chip key={c} on={channel === c} dim animation="quick" hoverStyle={{ borderColor: '$primary' }} pressStyle={{ scale: 0.96 }} onPress={() => setChannel(c)}>
                {t(CHANNEL_LABEL[c])}
              </Chip>
            ))}
          </XStack>

          {isLoading && posts.length === 0 ? (
            // shimmer post placeholders instead of a bare ellipsis
            <YStack gap={20}>
              {[0, 1].map((i) => (
                <Card key={i} padding={16} gap={12}>
                  <XStack gap={10} alignItems="center">
                    <Skeleton width={36} height={36} borderRadius={999} />
                    <YStack flex={1} gap={6}>
                      <Skeleton width="40%" height={11} />
                      <Skeleton width="24%" height={9} />
                    </YStack>
                  </XStack>
                  <SkeletonLines lines={3} />
                </Card>
              ))}
            </YStack>
          ) : isError && posts.length === 0 ? (
            <Card padding={40} alignItems="center" gap={6}>
              <Text fontSize={14} fontWeight="700" color="$color">
                {l('feed.error')}
              </Text>
              <Text fontSize={13} color="$muted">
                {l('feed.empty')}
              </Text>
            </Card>
          ) : posts.length === 0 ? (
            <Card padding={40} alignItems="center">
              <Text fontSize={13} color="$muted">
                {l('feed.empty')}
              </Text>
            </Card>
          ) : (
            <>
              {posts.map((p, i) => (
                <Enter key={`post-${p.id}`} index={Math.min(i, 11)}>
                  <PostCard post={p} onOpen={() => router.push(`/post/${p.id}`)} />
                </Enter>
              ))}
              {hasMorePosts && (
                <Button alignSelf="center" disabled={isFetchingMorePosts} onPress={() => fetchMorePosts()}>
                  {isFetchingMorePosts ? t({ ru: 'Загрузка…', en: 'Loading…' }) : t({ ru: 'Показать ещё', en: 'Show more' })}
                </Button>
              )}
            </>
          )}
        </YStack>
      )}
      </FadeIn>
    </Page>
  )
}
